<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    :closeOnScrim="false"
    placement="center"
    wide
  >
    <template #header>
      <div class="header">
        <div class="header-title">
          <span class="header-icon" aria-hidden="true">{{ ICONS.states.warning }}</span>
          <div style="min-width: 0;">
            <div class="header-text">Random Event</div>
            <div class="text-muted header-sub">{{ event.activityContext }}</div>
          </div>
        </div>
      </div>
    </template>

    <UiPanel>
      <UiSectionHeader>
        <template #title>Outcomes</template>
      </UiSectionHeader>

      <div class="outcomes">
        <div v-for="(o, idx) in event.outcomes" :key="idx" class="outcome">
          <div class="outcome-title">{{ o.message }}</div>
          <div v-if="renderOutcomeDetail(o)" class="outcome-detail text-muted">
            {{ renderOutcomeDetail(o) }}
          </div>
        </div>
      </div>
    </UiPanel>

    <div class="row wrap" style="justify-content: flex-end;">
      <UiButton size="sm" variant="secondary" :disabled="busy" @click="acknowledge">
        <template #icon>{{ ICONS.states.active }}</template>
        OK
      </UiButton>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { RandomEventOutcome, RandomEventResults } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const props = defineProps<{
  overlayZIndex: number;
  event: RandomEventResults;
  onAcknowledge: () => Promise<void> | void;
}>();

const emit = defineEmits<{ close: [] }>();
const busy = ref(false);

function renderOutcomeDetail(o: RandomEventOutcome): string | null {
  if (o.type === "gold_drop") return `Lost ${o.goldLost} gold.`;
  if (o.type === "restore") return `Restored ${o.hpRestored} HP and ${o.manaRestored} mana.`;
  if (o.type === "combat_xp") return `Gained ${o.xpGranted} Character XP.`;
  if (o.type === "loot_found") {
    if (!o.drops?.length) return "Nothing found.";
    return o.drops.map((d) => `${d.qty}× ${d.itemId}`).join(" · ");
  }
  if (o.type === "attr_mod") return o.changes.map((c) => `${String(c.attr).toUpperCase()} ${c.delta >= 0 ? "+" : ""}${c.delta} (${c.durationHours}h)`).join(" · ");
  if (o.type === "skill_mod") return o.changes.map((c) => `${c.skillId} ${c.delta >= 0 ? "+" : ""}${c.delta} (${c.durationHours}h)`).join(" · ");
  return null;
}

async function acknowledge() {
  if (busy.value) return;
  busy.value = true;
  try {
    await props.onAcknowledge();
    emit("close");
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.header-title {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  min-width: 0;
}

.header-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-2);
  display: grid;
  place-items: center;
  background: var(--surface-ghost-bg);
  border: 1px solid color-mix(in srgb, var(--state-warning) 22%, var(--border-0));
  box-shadow: var(--shadow-0);
  font-size: 1.25rem;
}

.header-text {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  line-height: 1.1;
}

.header-sub {
  margin-top: 0.2rem;
  font-size: 0.9rem;
}

.outcomes {
  margin-top: var(--space-sm);
  display: grid;
  gap: 0.65rem;
}

.outcome {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-2);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.7rem 0.75rem;
}

.outcome-title {
  font-weight: 750;
}

.outcome-detail {
  margin-top: 0.25rem;
  font-size: 0.9rem;
}
</style>
